import React, { FC, useMemo, useState, useEffect } from 'react'

import dayjs from 'dayjs'
import { useRouter } from 'next/navigation'
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  Legend,
  ResponsiveContainer,
  LabelList,
  Customized,
  Text as RechartsText,
  CartesianGrid,
} from 'recharts'

import { ChartChangeYearButtons, ProjectUsageTrendChartLegend } from 'src/components/molecules'

import { useAuthority } from 'src/hooks'
import { Routes } from 'src/routes'
import { ProjectCountHistoryEntity } from 'src/types'

type Props = {
  projectCountHistories: ProjectCountHistoryEntity[]
  className?: string
}

// 棒グラフ1つに必要なデータの型
type BarDataType = Omit<ProjectCountHistoryEntity, 'activatedCount'> & {
  xAxisLabel: string
  activatedCount: number | null
}

export const ProjectUsageTrendChart: FC<Props> = ({ projectCountHistories, className }) => {
  const router = useRouter()
  const { canReadReport, isLoading } = useAuthority()
  const [selectedYear, setSelectedYear] = useState(dayjs().year())

  useEffect(() => {
    if (isLoading) {
      return
    }

    // アクセス出来る権限がないユーザーの場合はダッシュボードにリダイレクト
    if (!canReadReport) {
      router.replace(Routes.Dashboard.path)
    }
  }, [canReadReport, isLoading, router])

  const targetProjectCountHistories = useMemo(() => {
    return projectCountHistories.filter(history => history.year === selectedYear)
  }, [projectCountHistories, selectedYear])

  const maxActiveCount = useMemo(() => {
    return Math.max(...targetProjectCountHistories.map(history => history.activatedCount))
  }, [targetProjectCountHistories])

  // 1〜12月のデータを作成する
  const displayProjectCountHistories = useMemo(() => {
    return Array.from({ length: 12 }).map((_, index): BarDataType => {
      const targetProjectCountHistory = targetProjectCountHistories.find(
        history => history.month === index + 1
      )
      return targetProjectCountHistory
        ? { ...targetProjectCountHistory, xAxisLabel: `${targetProjectCountHistory.month}月` }
        : {
            year: selectedYear,
            month: index + 1,
            // データがない月はnullを設定する事でLabelを表示しないようにする
            activatedCount: null,
            xAxisLabel: `${index + 1}月`,
          }
    })
  }, [targetProjectCountHistories, selectedYear])

  const oldestYear = useMemo(() => {
    return Math.min(...projectCountHistories.map(history => history.year))
  }, [projectCountHistories])

  const isCurrentMonth = (year: number, month: number): boolean => {
    const now = new Date()
    return now.getFullYear() === year && now.getMonth() === month - 1
  }

  const gradient = (
    <linearGradient id="gradient" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor="#05A19A" />
      <stop offset="100%" stopColor="#97BA3C" />
    </linearGradient>
  )

  const renderYearLabel = (props: any) => {
    // eslint-disable-next-line react/prop-types
    const { width, height } = props
    return (
      <RechartsText
        x={width / 2}
        y={height / 2}
        style={{
          fontFamily: 'var(--font-rubik)',
          fontSize: 'var(--s7)',
          transform: 'translateX(-10%)',
          fontWeight: 'bold',
          fill: '#F2F5F9',
        }}
      >
        {selectedYear}
      </RechartsText>
    )
  }

  const renderCustomizedLabel = (props: any) => {
    // eslint-disable-next-line react/prop-types
    const { x, y, width, value, fill } = props
    // 値がない場合はLabelを表示しない
    if (!value) return <g />

    return (
      <g>
        <text
          x={x + width / 2}
          y={y - 12}
          textAnchor="middle"
          dominantBaseline="middle"
          style={{
            fontFamily: 'var(--font-rubik)',
            fontSize: 'var(--s1)',
            fontWeight: 'bold',
            // fillの値は該当月のbarのカラーが入っている
            // そのため今月の場合のみ、url(#gradient) が指定されている
            fill: fill === 'url(#gradient)' ? 'url(#gradient)' : '#5E6B7B',
          }}
        >
          {value}
        </text>
      </g>
    )
  }

  return (
    <div className={className}>
      <div className="tw-relative">
        <ResponsiveContainer aspect={2.4}>
          <BarChart data={displayProjectCountHistories}>
            <defs>{gradient}</defs>
            <XAxis dataKey="xAxisLabel" axisLine={false} tickSize={0} tickMargin={10} />
            <YAxis
              type="number"
              tickCount={8}
              tickMargin={5}
              axisLine={false}
              tickSize={0}
              // 小数は表示しない
              allowDecimals={false}
              // 最大値に対して8%上に余白を持たせる。また切り上げを実行することで最大値が小さい場合でも余白が出来るようにする
              domain={[0, Math.ceil(maxActiveCount * 1.08)]}
            />
            <Legend content={<ProjectUsageTrendChartLegend className="tw-mt-s2" />} />
            <CartesianGrid vertical={false} style={{ stroke: '#E2E5EA' }} />
            <Customized component={renderYearLabel} />
            <Bar dataKey="activatedCount" barSize={16}>
              <LabelList dataKey="activatedCount" content={renderCustomizedLabel} />
              {displayProjectCountHistories.map(history => (
                <Cell
                  key={`${history.year}-${history.month}`}
                  fill={
                    isCurrentMonth(history.year, history.month)
                      ? 'var(--color-text-standard-disable)'
                      : 'url(#gradient)'
                  }
                  style={
                    isCurrentMonth(history.year, history.month)
                      ? undefined
                      : { filter: 'drop-shadow(0px 0px 10px rgba(122, 137, 160, 0.2))' }
                  }
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <ChartChangeYearButtons
        selectedYear={selectedYear}
        oldestYear={oldestYear}
        onClickPrevYear={() => setSelectedYear(selectedYear - 1)}
        onClickNextYear={() => setSelectedYear(selectedYear + 1)}
        className="tw-mx-s4 -tw-mt-s3"
      />
    </div>
  )
}
